import { DEFAULT_AXIS_LABEL_FONT_SIZE } from "constants/chart.constants";

import { processAxisLabels } from "./mosaicUtils";

/**
 * Get x axis labels for mosaic chart without overlap
 *
 * @param {any} series series data
 * @param {any} layout layout of the chart containing all xAxis labels
 * @param {number} width width of the chart
 * @param {number} gridRight right grid space
 * @param {number} gridLeft left grid space
 * @param {number} fontSize needed to space correctly for screenshot
 * @param {number} rotation label rotation angle
 * @param {number} xMin visible range minimum (from zoom state); defaults to 0
 * @param {number} xMax visible range maximum (from zoom state); defaults to full data max
 * @returns {any[]}  array of x axis labels
 */
export const getMosaicLabels = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  series: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layout: any,
  width: number,
  gridRight: number,
  gridLeft: number,
  fontSize: number = DEFAULT_AXIS_LABEL_FONT_SIZE,
  rotation: number = 0,
  xMin?: number,
  xMax?: number
) => {
  const labels = layout.xAxis.labels;

  const fullMax = Math.max(...series.map((s) => s.x[1]));
  const effectiveMax = xMax ?? fullMax;
  const effectiveMin = xMin ?? 0;

  const chartDataAreaWidth = width - gridRight - gridLeft;

  // When zoomed in, the leftmost or rightmost visible section may have its center
  // (the normal label position) outside the viewport. Inject a synthetic label at
  // the center of the visible portion so the section still gets labeled.
  let labelsToProcess = labels;
  if (xMin !== undefined || xMax !== undefined) {
    // value is number so ECharts' tickValuesToNumbers takes the numeric path (not the
    // string→indexOf path that crashes when axis.model.get('data') is undefined for
    // value-type axes).
    const synthetic: { label: string; value: number }[] = [];

    if (xMin !== undefined) {
      const leftSection = series.find((s) => s.x[0] < xMin && s.x[1] > xMin);
      if (leftSection) {
        const sectionAlreadyLabeled = labels.some((l) => {
          const v = parseFloat(l.value);
          return (
            v >= xMin &&
            v <= effectiveMax &&
            v >= leftSection.x[0] &&
            v <= leftSection.x[1]
          );
        });
        if (!sectionAlreadyLabeled) {
          const edgeLabel = labels.find((l) => {
            const v = parseFloat(l.value);
            return v >= leftSection.x[0] && v <= leftSection.x[1] && v < xMin;
          });
          if (edgeLabel) {
            const visibleEnd = Math.min(leftSection.x[1], effectiveMax);
            synthetic.push({ label: edgeLabel.label, value: (xMin + visibleEnd) / 2 });
          }
        }
      }
    }

    if (xMax !== undefined) {
      const rightSection = series.find((s) => s.x[0] < xMax && s.x[1] > xMax);
      if (rightSection) {
        const sectionAlreadyLabeled = labels.some((l) => {
          const v = parseFloat(l.value);
          return (
            v >= effectiveMin &&
            v <= xMax &&
            v >= rightSection.x[0] &&
            v <= rightSection.x[1]
          );
        });
        if (!sectionAlreadyLabeled) {
          const edgeLabel = labels.find((l) => {
            const v = parseFloat(l.value);
            return v >= rightSection.x[0] && v <= rightSection.x[1] && v > xMax;
          });
          if (edgeLabel) {
            const visibleStart = Math.max(rightSection.x[0], effectiveMin);
            synthetic.push({ label: edgeLabel.label, value: (visibleStart + xMax) / 2 });
          }
        }
      }
    }

    if (synthetic.length > 0) {
      labelsToProcess = [...labels, ...synthetic].sort(
        (a, b) => parseFloat(a.value) - parseFloat(b.value)
      );
    }
  }

  return processAxisLabels(
    labelsToProcess,
    effectiveMax,
    chartDataAreaWidth,
    fontSize,
    rotation,
    effectiveMin
  );
};
